import { buildChatBotInstallationSnippets } from "@jugl-web/domain-resources/chat-bot/utils/buildChatBotInstallationSnippets";
import { PlainButton, Tooltip } from "@jugl-web/ui-components";
import { cx, useTranslations } from "@jugl-web/utils";
import { useClipboard } from "@jugl-web/utils/hooks/useClipboard";
import { environment } from "@web-src/environments/environment";
import { useWebChatBotAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider/useWebChatBotAccount";
import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { CRMAccountCard } from "../../../../components/CRMAccountCard";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { ReactComponent as CheckIcon } from "./assets/check.svg";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";
import { ReactComponent as InstallIcon } from "./assets/install.svg";

const BOT_JS_URL = environment.chatBotUrl;

export const InstallTabContent: React.FC = () => {
  const { t } = useTranslations();
  const { copyToClipboard } = useClipboard();
  const [isCopied, setIsCopied] = useState(false);
  const [isMobileUrlCopied, setIsMobileUrlCopied] = useState(false);

  const { webChatBotAccount } = useWebChatBotAccount();

  const { webScript, mobileUrl, mobileCode } = useMemo(
    () =>
      buildChatBotInstallationSnippets({
        token: webChatBotAccount?.token ?? "",
        chatbotUrl: BOT_JS_URL,
      }),
    [webChatBotAccount?.token]
  );

  const handleCopy = useCallback(() => {
    copyToClipboard(webScript);
    setIsCopied(true);

    setTimeout(() => {
      setIsCopied(false);
    }, 5000);
  }, [webScript, copyToClipboard]);

  const handleMobileCopy = useCallback(() => {
    copyToClipboard(mobileUrl);
    setIsMobileUrlCopied(true);

    setTimeout(() => {
      setIsMobileUrlCopied(false);
    }, 5000);
  }, [mobileUrl, copyToClipboard]);

  return (
    <div className="flex animate-fade-in flex-col gap-8 bg-grey-100 p-8">
      <CRMAccountCard
        title={t({
          id: "client-support-page.install-messenger-on-your-site",
          defaultMessage: "Install the messenger on your site or app",
        })}
        description={t({
          id: "client-support-page.enable-live-chat-on-your-website",
          defaultMessage:
            "Enable live chat on your website and manage conversations in Jugl CRM",
        })}
        icon={InstallIcon}
      />
      <CRMInfoSection
        title={t({
          id: "client-support-page.how-to-install",
          defaultMessage: "How to install:",
        })}
        subtitle={
          <ul className="m-0 space-y-1.5 pl-5 font-secondary text-sm leading-[140%] tracking-[0.14px] text-dark-800">
            <li>
              {t(
                {
                  id: "client-support-page.paste-code-snippet-before-tag",
                  defaultMessage: `Paste this code snippet before the {bodyTag} tag on every page you want messenger to appear`,
                },
                { bodyTag: "</body>" }
              )}
            </li>
            <li>
              {t({
                id: "client-support-page.must-be-done-on-client-side",
                defaultMessage: "This must be done in your client-side code ℹ️",
              })}
            </li>
            <li>
              {t({
                id: "client-support-page.reload-webiste-to-appear-web-chat",
                defaultMessage:
                  "Once your done - reload the website and web chat should appear in bottom left or right corner",
              })}
            </li>
            <li>
              {t(
                {
                  id: "client-support-page.chat-is-not-appearing",
                  defaultMessage:
                    "If chat is not appearing - please try again or reach out to <email>support</email>",
                },
                {
                  email: (text: ReactNode) => (
                    <a
                      className="text-[13px] leading-[150%] font-medium tracking-[0.13px] text-primary-800 underline"
                      href="mailto:support@jugl.com"
                    >
                      {text}
                    </a>
                  ),
                }
              )}
            </li>
          </ul>
        }
      >
        <div className="flex items-start gap-3 rounded-2xl border border-solid border-dark-200 bg-grey-100 p-4">
          <pre className="grow">
            <code className="m-0 font-secondary text-sm leading-[140%] tracking-[0.14px] break-all whitespace-pre-wrap text-dark">
              {webScript}
            </code>
          </pre>
          <Tooltip
            renderTrigger={({ props, ref }) => (
              <PlainButton
                className={cx(
                  "flex h-8 w-8 items-center justify-center rounded-lg bg-grey-200 transition-colors hover:bg-grey-300",
                  isCopied && "bg-[#E8E0F4] hover:bg-[#E8E0F4]"
                )}
                onClick={handleCopy}
                ref={ref}
                {...props}
                disabled={isCopied}
              >
                {isCopied ? <CheckIcon /> : <CopyIcon />}
              </PlainButton>
            )}
          >
            {isCopied
              ? t({
                  id: "common.copied",
                  defaultMessage: "Copied",
                })
              : t({
                  id: "common.copy",
                  defaultMessage: "Copy",
                })}
          </Tooltip>
        </div>
      </CRMInfoSection>
      <CRMInfoSection
        title={t({
          id: "client-support-page.install-on-mobile",
          defaultMessage: "Install on Mobile:",
        })}
        subtitle={
          <span className="font-secondary text-sm leading-[140%] tracking-[0.14px] text-dark-800">
            {t({
              id: "client-support-page.open-url-in-webview",
              defaultMessage:
                "Open this URL in your mobile app's WebView to display the chat",
            })}
          </span>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-2xl border border-solid border-dark-200 bg-grey-100 p-4">
            <pre className="grow">
              <code className="m-0 font-secondary text-sm leading-[140%] tracking-[0.14px] break-all whitespace-pre-wrap text-dark">
                {mobileUrl}
              </code>
            </pre>
            <Tooltip
              renderTrigger={({ props, ref }) => (
                <PlainButton
                  className={cx(
                    "flex h-8 w-8 items-center justify-center rounded-lg bg-grey-200 transition-colors hover:bg-grey-300",
                    isMobileUrlCopied && "bg-[#E8E0F4] hover:bg-[#E8E0F4]"
                  )}
                  onClick={handleMobileCopy}
                  ref={ref}
                  {...props}
                  disabled={isMobileUrlCopied}
                >
                  {isMobileUrlCopied ? <CheckIcon /> : <CopyIcon />}
                </PlainButton>
              )}
            >
              {isMobileUrlCopied
                ? t({
                    id: "common.copied",
                    defaultMessage: "Copied",
                  })
                : t({
                    id: "common.copy",
                    defaultMessage: "Copy",
                  })}
            </Tooltip>
          </div>
          <div className="rounded-2xl border border-solid border-dark-200 bg-grey-100 p-4">
            <pre className="m-0">
              <code className="font-secondary text-sm leading-[140%] tracking-[0.14px] break-all whitespace-pre-wrap text-dark">
                {mobileCode}
              </code>
            </pre>
          </div>
        </div>
      </CRMInfoSection>
    </div>
  );
};
